import { AppException, ErrorCode } from './AppException';

import type { ExceptionDetails } from './AppException';

/**
 * Validation Exception
 * Used for form validation and input validation errors
 */
export class ValidationException extends AppException {
  public readonly validationErrors?: Record<string, string | string[]> | undefined;

  constructor(
    details: Omit<ExceptionDetails, 'code'> & {
      code?: ErrorCode.VALIDATION_ERROR | ErrorCode.INVALID_INPUT | ErrorCode.MISSING_REQUIRED_FIELD;
      validationErrors?: Record<string, string | string[]> | undefined;
    }
  ) {
    super({
      ...details,
      code: details.code ?? ErrorCode.VALIDATION_ERROR,
      statusCode: 400,
    });
    this.name = 'ValidationException';
    this.validationErrors = details.validationErrors;
  }

  static invalidField(field: string, message: string): ValidationException {
    return new ValidationException({
      message: `Invalid ${field}: ${message}`,
      userMessage: message,
      field,
    });
  }

  static requiredField(field: string): ValidationException {
    return new ValidationException({
      code: ErrorCode.MISSING_REQUIRED_FIELD,
      message: `Missing required field: ${field}`,
      userMessage: `${field} is required`,
      field,
    });
  }

  static fromApiErrors(
    errors: Record<string, string | string[]>,
    message = 'Validation failed'
  ): ValidationException {
    return new ValidationException({
      message,
      userMessage: 'Please check the form for errors',
      validationErrors: errors,
    });
  }
}

/**
 * Authentication Exception
 * Used for login failures, expired sessions, and invalid tokens
 */
export class AuthenticationException extends AppException {
  constructor(
    messageOrDetails: string | (Omit<ExceptionDetails, 'code'> & { code?: ErrorCode.AUTHENTICATION_FAILED | ErrorCode.SESSION_EXPIRED | ErrorCode.INVALID_CREDENTIALS })
  ) {
    const details = typeof messageOrDetails === 'string'
      ? { message: messageOrDetails, userMessage: messageOrDetails }
      : messageOrDetails;
    super({
      ...details,
      code: details.code ?? ErrorCode.AUTHENTICATION_FAILED,
      statusCode: 401,
    });
    this.name = 'AuthenticationException';
  }

  static sessionExpired(): AuthenticationException {
    return new AuthenticationException({
      code: ErrorCode.SESSION_EXPIRED,
      message: 'User session has expired',
      userMessage: 'Your session has expired. Please log in again.',
    });
  }

  static invalidCredentials(): AuthenticationException {
    return new AuthenticationException({
      code: ErrorCode.INVALID_CREDENTIALS,
      message: 'Invalid credentials provided',
      userMessage: 'Invalid email or password. Please try again.',
    });
  }
}

/**
 * Authorization Exception
 * Used for permission denied scenarios
 */
export class AuthorizationException extends AppException {
  constructor(details: Omit<ExceptionDetails, 'code'> & { code?: ErrorCode.AUTHORIZATION_FAILED | ErrorCode.INSUFFICIENT_PERMISSIONS }) {
    super({
      ...details,
      code: details.code ?? ErrorCode.AUTHORIZATION_FAILED,
      statusCode: 403,
    });
    this.name = 'AuthorizationException';
  }

  static insufficientPermissions(resource: string): AuthorizationException {
    return new AuthorizationException({
      code: ErrorCode.INSUFFICIENT_PERMISSIONS,
      message: `Insufficient permissions to access ${resource}`,
      userMessage: 'You do not have permission to perform this action.',
      metadata: { resource },
    });
  }
}

/**
 * Not Found Exception
 * Used when a requested resource doesn't exist
 */
export class NotFoundException extends AppException {
  constructor(
    details: Omit<ExceptionDetails, 'code'> & { code?: ErrorCode.RESOURCE_NOT_FOUND | ErrorCode.CASE_NOT_FOUND | ErrorCode.USER_NOT_FOUND }
  ) {
    super({
      ...details,
      code: details.code ?? ErrorCode.RESOURCE_NOT_FOUND,
      statusCode: 404,
    });
    this.name = 'NotFoundException';
  }

  static case(caseId: string): NotFoundException {
    return new NotFoundException({
      code: ErrorCode.CASE_NOT_FOUND,
      message: `Case with ID ${caseId} not found`,
      userMessage: 'The requested case could not be found.',
      metadata: { caseId },
    });
  }

  static resource(resourceType: string, id: string): NotFoundException {
    return new NotFoundException({
      message: `${resourceType} with ID ${id} not found`,
      userMessage: `The requested ${resourceType.toLowerCase()} could not be found.`,
      metadata: { resourceType, id },
    });
  }
}

/**
 * Network Exception
 * Used for network connectivity issues
 */
export class NetworkException extends AppException {
  constructor(
    messageOrDetails: string | (Omit<ExceptionDetails, 'code'> & { code?: ErrorCode.NETWORK_ERROR | ErrorCode.TIMEOUT_ERROR | ErrorCode.CONNECTION_REFUSED })
  ) {
    const details = typeof messageOrDetails === 'string'
      ? { message: messageOrDetails, userMessage: messageOrDetails }
      : messageOrDetails;
    super({
      ...details,
      code: details.code ?? ErrorCode.NETWORK_ERROR,
    });
    this.name = 'NetworkException';
  }

  static connectionError(): NetworkException {
    return new NetworkException({
      message: 'Network connection failed',
      userMessage: 'Unable to connect to the server. Please check your internet connection.',
    });
  }

  static timeout(): NetworkException {
    return new NetworkException({
      code: ErrorCode.TIMEOUT_ERROR,
      message: 'Request timed out',
      userMessage: 'The request took too long. Please try again.',
    });
  }
}

/**
 * Server Exception
 * Used for server-side errors (500+)
 */
export class ServerException extends AppException {
  constructor(
    messageOrDetails: string | (Omit<ExceptionDetails, 'code'> & { code?: ErrorCode.SERVER_ERROR | ErrorCode.SERVICE_UNAVAILABLE })
  ) {
    const details = typeof messageOrDetails === 'string'
      ? { message: messageOrDetails, userMessage: messageOrDetails }
      : messageOrDetails;
    super({
      ...details,
      code: details.code ?? ErrorCode.SERVER_ERROR,
      statusCode: details.statusCode ?? 500,
    });
    this.name = 'ServerException';
  }

  static internalError(originalError?: unknown): ServerException {
    return new ServerException({
      message: 'Internal server error',
      userMessage: 'Something went wrong on our end. Please try again later.',
      originalError,
    });
  }

  static serviceUnavailable(): ServerException {
    return new ServerException({
      code: ErrorCode.SERVICE_UNAVAILABLE,
      statusCode: 503,
      message: 'Service temporarily unavailable',
      userMessage: 'The service is temporarily unavailable. Please try again in a few minutes.',
    });
  }
}

